import { useEffect, useContext, useCallback, useRef } from 'react'
import { FronthubContext } from 'front-hub-context-api'
import { CONTEXT } from '../../../../constants'
import FronthubError from '../../../../core/helpers/FronthubError'
import communication, {
  DEFAULT_CHANNEL,
  normalizeEvent,
} from '../../communication-api'

function usePrevious(value) {
  const ref = useRef()

  useEffect(() => {
    ref.current = value
  }, [value])

  return ref.current
}

const listen = (event, callback) => communication('listen', event, callback)
const fronthubEmit = (event, payload) => communication('emit', event, payload)

export default function useCommunication(channel = DEFAULT_CHANNEL) {
  const {
    [CONTEXT]: {
      configuration: { name: microfrontendName },
    },
    events: {
      listeners: listenersFromConfiguration,
      emitters: emittersFromConfiguration,
    },
  } = useContext(FronthubContext)

  const normalizeEvents = useCallback(
    (messages = [], channel) =>
      messages.map(message => normalizeEvent(message, channel)),
    [normalizeEvent],
  )

  const listeners = normalizeEvents(listenersFromConfiguration, channel)
  const emitters = normalizeEvents(emittersFromConfiguration, channel)

  const useListener = useCallback(
    (message, callback, deps = []) => {
      const event = normalizeEvent(message, channel)

      if (!listeners.includes(event)) {
        throw new FronthubError(
          'you must include the event ' +
            event +
            ' inside front-hub.config.js key events.listeners[] from micro frontend ' +
            microfrontendName,
        )
      }

      const memoCallback = useCallback(callback, deps)
      const previousCallback = usePrevious(memoCallback)

      const unlisten = listen(event, memoCallback)
      const previousUnlisten = usePrevious(unlisten)

      if (memoCallback !== previousCallback) {
        previousUnlisten?.()
      }

      return unlisten
    },
    [microfrontendName, normalizeEvent],
  )

  const emit = (message, payload) => {
    const event = normalizeEvent(message, channel)

    if (!emitters.includes(event)) {
      throw new FronthubError(
        'you must include the event ' +
          event +
          ' inside front-hub.config.js key events.emitters[] from micro frontend ' +
          microfrontendName,
      )
    }

    fronthubEmit(event, payload)
  }

  return {
    useListener,
    emit,
  }
}
